import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import AdminDialog, {AdminDialogProperties, AdminDialogState} from "./AdminDialog";
import AdminDialogType from "../AdminDialogType";
import Message from "../../../shared/log/Message";
import {MessageType} from "../../../shared/log/MessageInterface";
import TruststoreDialogFields from "../field/TruststoreDialogFields";
import {Password} from "primereact/password";
import {
	CertificateEntry,
	ConfigurationType,
	Server,
	ServerCheckMode,
	TrustStoreKeyStore,
	TrustStoreKeyStoreInterface,
	TruststoreServer,
	TrustStoreServerCheck,
	TrustStoreServerCheckInterface
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import FileInput, {File} from "../../../shared/components/FileInput";
import {Dropdown} from "primereact/dropdown";
import Administration from "../../session/Administration";
import {KeyStore, KeystoreType} from "@softvision/webpdf-wsclient-9-typescript/lib/session/Administration";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import {KeystoreViewer} from "../../../shared/components/KeystoreViewer";
import Translate from "../../../shared/translation/Translate";
import Helper from "../../../shared/components/Helper";
import Icon from "../../../shared/components/Icon";
import Info, {InfoType} from "../../../shared/components/Info";

export enum TruststoreType {
	NONE = "none",
	JKS = "jks"
}

interface TruststoreDialogProperties extends AdminDialogProperties<TruststoreServer> {
}

interface TruststoreDialogStatus extends AdminDialogState<TruststoreServer> {
	originalkeystoreType: TruststoreType;
	keystoreType: TruststoreType;
	keystoreFile: File;
	keystoreSet: boolean;
	certificates?: Array<CertificateEntry>;
}

export class TruststoreDialogDefinition extends AdminDialog<TruststoreServer, TruststoreDialogProperties,
	TruststoreDialogStatus> {
	private readonly DEFAULT_FILENAME: string = "truststore";

	constructor(props: Readonly<TruststoreDialogProperties>) {
		super(props);

		this.state = {
			...this.state,
			keystoreType: TruststoreType.NONE,
			keystoreFile: {
				name: "",
				mimetype: "",
				value: ""
			},
			keystoreSet: false
		};
	}

	private renderCertificateContents(): ReactNode {
		if (!this.state.keystoreSet) {
			return null;
		}

		return (
			<>
				<div className="p-col-12">
					<KeystoreViewer {...this.props} certificates={this.state.certificates}/>
				</div>
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("truststore.new", {ns: "dialog"})}
							className="dialog-button"
							onClick={() => {
								this.onChange(TruststoreDialogFields.PASSWORD, "");
								this.onChange(TruststoreDialogFields.FILE, undefined);

								this.setState({
									keystoreSet: false
								});
							}}/>
				</div>
			</>
		);
	}

	public renderFormContents(): ReactNode {
		let parameter: TruststoreServer | undefined = this.state.parameter;

		if (!FunctionHelper.exists(parameter) || this.state.keystoreSet) {
			return null;
		}

		return (
			<fieldset className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-type">
						<Translate {...this.props} data="truststore.type.label" namespace="dialog"/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<Dropdown value={this.state.keystoreType} inputId="control-type"
								  options={[
									  {
										  label: Translate.value("truststore.type.none", {ns: "dialog"}),
										  value: TruststoreType.NONE
									  },
									  {
										  label: Translate.value("truststore.type.jks", {ns: "dialog"}),
										  value: TruststoreType.JKS
									  }
								  ]}
								  onChange={
									  (e) => this.onChange(TruststoreDialogFields.TYPE, e.value)
								  }
								  appendTo={document.getElementById("root")}/>
					</div>
				</div>
				<div className="p-col-12 p-grid" hidden={this.state.keystoreType === TruststoreType.NONE}>
					<label className="p-col-12 p-sm-4" htmlFor="control-file">
						<Translate {...this.props} data="truststore.file" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={TruststoreServer.getFileDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8">
						<FileInput inputId="control-file" accept=".jks" value={this.state.keystoreFile.value}
								   filename={this.state.keystoreFile.name}
								   onChange={(files) => this.onChange(TruststoreDialogFields.FILE, files[0])}/>
					</div>
				</div>
				<div className="p-col-12 p-grid" hidden={this.state.keystoreType === TruststoreType.NONE}>
					<label className="p-col-12 p-sm-4" htmlFor="control-password">
						<Translate {...this.props} data="truststore.password" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={TruststoreServer.getPasswordDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<Password inputId="control-password" feedback={false} toggleMask={true}
								  value={FunctionHelper.applyValue(
									  parameter?.password, TruststoreServer.getPasswordDefault()
								  )}
								  onChange={(e) => this.onChange(
									  TruststoreDialogFields.PASSWORD, e.currentTarget.value
								  )}/>
					</div>
				</div>
			</fieldset>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["truststore-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.admin.truststore.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderFormContents()}
					{this.renderCertificateContents()}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-certificate"}/>
					<Translate {...this.props} data="truststore.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected onChange(field: TruststoreDialogFields, value: any): void {
		let parameter: TruststoreServer = this.state.parameter as TruststoreServer;

		switch (field) {
			case TruststoreDialogFields.TYPE:
				parameter.password = TruststoreServer.getPasswordDefault();

				this.setState({
					keystoreType: value
				});
				break;
			case TruststoreDialogFields.FILE:
				if (!FunctionHelper.exists(value)) {
					parameter.file = undefined;
					return;
				}

				let keystoreFile: File = value;
				parameter.file = this.DEFAULT_FILENAME;

				this.setState({
					keystoreFile: keystoreFile
				});
				break;
			case TruststoreDialogFields.PASSWORD:
				parameter.password = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		}, () => {
			super.onChange(field, value);
		});
	}

	protected hasChanges(): boolean {
		let result: boolean = super.hasChanges();

		if (this.state.keystoreType !== this.state.originalkeystoreType) {
			result = true;
		}

		if (this.state.keystoreFile.value !== "") {
			result = true;
		}

		return result;
	}

	private async isKeystoreSet(): Promise<void> {
		let administration: Administration = this.props.context.session.getAdministration();
		let trustStoreKeyStore: TrustStoreKeyStore =
			await administration.getKeystore(KeystoreType.Truststore) as TrustStoreKeyStore;

		this.setState({
			keystoreSet: FunctionHelper.applyValue(
				trustStoreKeyStore.certificates, TrustStoreKeyStore.getCertificatesDefault()
			).length > 0
		});
	}

	protected async onShow(): Promise<void> {
		await super.onShow();
		await this.isKeystoreSet();

		let parameter: TruststoreServer | undefined = this.state.parameter;
		let keystoreType: TruststoreType =
			!FunctionHelper.isEmpty(parameter?.file) ? TruststoreType.JKS : TruststoreType.NONE;

		let administration: Administration = this.props.context.session.getAdministration();
		let trustStoreKeyStore: TrustStoreKeyStore =
			await administration.getKeystore(KeystoreType.Truststore) as TrustStoreKeyStore;
		let certificates: Array<CertificateEntry> = FunctionHelper.applyValue(
			trustStoreKeyStore.certificates, TrustStoreKeyStore.getCertificatesDefault()
		);

		this.setState({
			originalkeystoreType: keystoreType,
			keystoreType: keystoreType,
			certificates: certificates
		});
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let administration: Administration = this.props.context.session.getAdministration();
		let serverConfiguration: Server = (
			await administration.getConfiguration(ConfigurationType.Server) as Server
		).clone();
		serverConfiguration.truststore =
			this.state.keystoreType === TruststoreType.JKS ? this.state.parameter : undefined;

		let currentTruststore: KeyStore = await administration.getKeystore(KeystoreType.Truststore);

		let checks: Array<TrustStoreServerCheck> = [];
		if (this.state.keystoreType !== TruststoreType.NONE) {
			checks.push(TrustStoreServerCheck.fromJson({
				checkType: ServerCheckMode.Truststore
			} as TrustStoreServerCheckInterface));
		}

		try {
			administration.setKeystore(KeystoreType.Truststore, TrustStoreKeyStore.fromJson({
				keyStoreContent: this.state.keystoreType === TruststoreType.JKS ?
					this.state.keystoreFile.value : undefined
			} as TrustStoreKeyStoreInterface));

			await administration.updateConfiguration(ConfigurationType.Server, serverConfiguration, checks);

			this.setState({
				hasChanges: false
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}

			// reset truststore
			administration.setKeystore(KeystoreType.Truststore, currentTruststore);
			return;
		}

		let message: Message = new Message("success.administration.truststore", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.props.context.getPage().setState({
			showRestartMessage: true
		});
		this.hide();
	}

	protected hide() {
		this.setState({
			keystoreFile: {
				name: "",
				mimetype: "",
				value: ""
			}
		});

		super.hide();
	}

	protected async reset(): Promise<void> {
		await super.reset();
		await this.isKeystoreSet();

		this.setState({
			keystoreFile: {
				name: "",
				mimetype: "",
				value: ""
			},
			keystoreType: this.state.originalkeystoreType
		});
	}

	protected getDialogType(): AdminDialogType {
		return AdminDialogType.TRUSTSTORE;
	}
}

const TruststoreDialog = React.memo(TruststoreDialogDefinition,
	(prevProps: Readonly<TruststoreDialogProperties>, nextProps: Readonly<TruststoreDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default TruststoreDialog;